import * as Sentry from '@sentry/react';
import { useCallback, useMemo } from 'react';

import type { UUID } from '@bugbug/core/types/aliases';
import type { RunMode } from '@bugbug/core/types/base';
import type { Profile } from '@bugbug/core/types/profiles';
import type { Test } from '@bugbug/core/types/tests';
import useModal from '~/hooks/useModal';
import { selectCurrentOrganizationId } from '~/modules/organization/organization.selectors';
import { useAppDispatch, useAppSelector } from '~/modules/store';
import { TestRunActions } from '~/modules/testRun/testRun.redux';
import { UIStateActions } from '~/modules/uiState/uiState.redux';
import { isApiError, isBadRequestError, isPaymentRequiredError } from '~/services/api/utils';
import toasts from '~/services/toasts';
import { ACTIONS } from '~/services/toasts/internalServerError';
import { useTestRunnerActionsState } from '~/views/TestDetails/components/TestActions/TestHeaderActions.hooks';

import { testsApi } from '../../tests.api';
import { showPendingTest } from '../../tests.utils';

interface RunTestParams {
  runMode: RunMode;
  startFromStepId: null;
  stopAfterStepId: null;
  stopAtGroupId: null;
  stopAction: 'finish';
  sessionId: UUID;
  runProfileId: Profile['id'];
  redirect: boolean;
}

export const useRunTest = () => {
  const dispatch = useAppDispatch();
  const modal = useModal();
  const [runTest, runTestState] = testsApi.useRunTestMutation();
  const organizationId = useAppSelector(selectCurrentOrganizationId);
  const { setPendingAction, resetPendingAction } = useTestRunnerActionsState();

  const handleRunTest = useCallback(
    async ({ testId, params }: { testId: Test['id']; params: RunTestParams }) => {
      const pendingToast = showPendingTest(ACTIONS.START);
      setPendingAction(params.runMode === 'server' ? 'runCloud' : 'runLocal');

      const data = {
        ...params,
        testId,
      };
      const result = runTest(data).unwrap();

      result
        .then((testRun) => {
          if (testRun) {
            pendingToast.dismiss();
            toasts.tests.showTestStarted(testRun, organizationId);
            dispatch(TestRunActions.setCurrentTestRun(testRun, params.redirect));
          }

          dispatch(UIStateActions.resetRecentlyAddedStepsIds());
        })
        .catch((error) => {
          if (isApiError(error) && !isBadRequestError(error)) {
            pendingToast.error({ content: Object.values(error.data || {})[0]?.message });

            if (isPaymentRequiredError(error)) {
              modal.show('upgrade_plan');
              dispatch(UIStateActions.disablePlaybackSelectedPositions());
              return;
            }
          } else {
            Sentry.captureException(error);
            pendingToast.error();
          }

          dispatch(UIStateActions.disablePlaybackSelectedPositions());
        })
        .finally(() => {
          resetPendingAction();
        });

      return result;
    },
    [dispatch, modal, organizationId, resetPendingAction, runTest, setPendingAction],
  );

  return useMemo(() => [handleRunTest, runTestState] as const, [handleRunTest, runTestState]);
};
